import {
  put,
  all,
  takeEvery,
  fork,
  select,
  call,
  take
} from "redux-saga/effects";
import { actions, getters } from "../model";
import { registerError } from "redux/modules/errors/actions";
import { apiCall } from "App/Data/sagas";
import { eventDetails as getEventDetails } from "redux/modules/event/selectors";
import { eventDetails as getPortalEventDetails } from "redux/modules/portal/selectors";
import { showSnackbar } from "redux/modules/snackbar/actions";
import {
  actions as FormActions,
  getters as FormGetters
} from "ui-kit/Form/model";
import fileUploadSagas from "./fileUploadSagas";
import { EDIT_FILE_TYPE_FIELD_ID, EDIT_TYPE_FORM_ID } from "../constants";
import * as R from "ramda";
import { push } from "react-router-redux";

const getParams = function*() {
  const viaPortal = yield select(getters.viaPortal);
  const eventDetails = yield select(getEventDetails);
  const portalEventDetails = yield select(getPortalEventDetails);

  return {
    eventDetails: viaPortal ? portalEventDetails : eventDetails,
    viaPortal
  };
};

const refreshContent = function*() {
  try {
    yield put(actions.setRefreshing(true));

    const { eventDetails } = yield call(getParams);
    const sessionId = yield select(getters.sessionId);

    const { payload: contentPayload } = yield call(apiCall, {
      method: "get",
      url: `/portal/event/${eventDetails.uuid}/session-content/${sessionId}/content`
    });

    yield put(actions.setContent(contentPayload));
  } catch (error) {
    yield all([
      put(
        registerError([
          {
            system: error,
            user: "An error occurred getting content"
          }
        ])
      )
    ]);
  } finally {
    yield put(actions.setRefreshing(false));
  }
};

const init = function*({ payload: { sessionId, viaPortal, accentColor } }) {
  try {
    yield all([
      put(actions.setLoading(true)),
      put(actions.setSessionId(sessionId)),
      put(actions.setViaPortal(viaPortal))
    ]);

    const { eventDetails } = yield call(getParams);

    const [
      { payload: settingsPayload },
      { payload: contentPayload }
    ] = yield all([
      call(apiCall, {
        method: "get",
        url: `/portal/event/${eventDetails.uuid}/session-content/settings`
      }),
      call(apiCall, {
        method: "get",
        url: `/portal/event/${eventDetails.uuid}/session-content/${sessionId}/content`
      })
    ]);

    yield put(
      actions.setInitialData({
        settings: settingsPayload,
        content: contentPayload,
        accentColor
      })
    );
  } catch (error) {
    yield all([
      put(
        registerError([
          {
            system: error,
            user: "An error occurred getting content settings"
          }
        ])
      )
    ]);
  } finally {
    yield put(actions.setLoading(false));
  }
};

const sendComment = function*({ payload: { newComment, idFileVersion } }) {
  const sessionId = yield select(getters.sessionId);
  const { eventDetails, viaPortal } = yield call(getParams);
  try {
    yield call(apiCall, {
      method: "post",
      url: `/portal/event/${eventDetails.uuid}/session-content/${sessionId}/content/${idFileVersion}/comments`,
      data: {
        message: newComment,
        role: viaPortal ? "participant" : "admin"
      }
    });

    yield call(refreshContent);
  } catch (error) {
    yield all([
      put(
        registerError([
          {
            system: error,
            user: "An error occurred sending your comment"
          }
        ])
      )
    ]);
  }
};

const downloadFile = function*({ payload: fileId }) {
  const content = yield select(getters.combinedContent);
  const file = content.find(c => c.id === fileId);
  window.open(file.url, "_blank");
};

const deleteFile = function*({ payload: fileId }) {
  const { eventDetails } = yield call(getParams);
  const sessionId = yield select(getters.sessionId);

  try {
    yield call(apiCall, {
      method: "delete",
      url: `/portal/event/${eventDetails.uuid}/session-content/${sessionId}/content/${fileId}`
    });
    yield call(refreshContent);
    yield put(showSnackbar({ message: "File version has been deleted" }));
  } catch (error) {
    yield all([
      put(
        registerError([
          {
            system: error,
            user: "An error occurred deleting file version"
          }
        ])
      )
    ]);
  }
};

const deleteAllVersions = function*({ payload: fileId }) {
  const { eventDetails } = yield call(getParams);
  const sessionId = yield select(getters.sessionId);

  try {
    yield call(apiCall, {
      method: "delete",
      url: `/portal/event/${eventDetails.uuid}/session-content/${sessionId}/content/${fileId}`,
      data: { deleteAllVersions: true }
    });
    yield call(refreshContent);
    yield put(showSnackbar({ message: "File has been deleted" }));
  } catch (error) {
    yield all([
      put(
        registerError([
          {
            system: error,
            user: "An error occurred deleting file"
          }
        ])
      )
    ]);
  }
};

const checkEditTypeModalOpening = function*({ payload }) {
  if (payload) {
    const content = yield select(getters.combinedContent);
    const file = content.find(c => c.id === payload);
    const shouldDisableButton =
      !file?.type ||
      !file?.bizzabo_settings?.position ||
      !file?.bizzabo_settings?.icon ||
      !file?.bizzabo_settings?.display_name;
    yield put(actions.setIsSaveBtnDisabled(shouldDisableButton));
  }
};

const saveUpdatedType = function*() {
  const sessionId = yield select(getters.sessionId);
  const fileId = yield select(getters.showEditTypeModal);
  const { eventDetails } = yield call(getParams);
  const fields = yield select(FormGetters.fields, {
    instanceId: EDIT_TYPE_FORM_ID
  });
  const settings = yield select(getters.settings);
  const type = R.prop(EDIT_FILE_TYPE_FIELD_ID.TYPE, fields);
  const data = {
    [EDIT_FILE_TYPE_FIELD_ID.TYPE]: type?.value?.length ? type.value[0] : null
  };
  if (settings?.bizzabo_integration_enabled) {
    data.bizzabo_settings = {
      [EDIT_FILE_TYPE_FIELD_ID.POSITION]: Number(
        R.prop(EDIT_FILE_TYPE_FIELD_ID.POSITION, fields)?.value || 0
      ),
      [EDIT_FILE_TYPE_FIELD_ID.ICON]: R.prop(
        EDIT_FILE_TYPE_FIELD_ID.ICON,
        fields
      )?.value?.[0]?.url,
      [EDIT_FILE_TYPE_FIELD_ID.DISPLAY_NAME]: R.prop(
        EDIT_FILE_TYPE_FIELD_ID.DISPLAY_NAME,
        fields
      )?.value
    };
  }

  try {
    yield call(apiCall, {
      method: "put",
      url: `/portal/event/${eventDetails.uuid}/session-content/${sessionId}/content/${fileId}`,
      data
    });
    yield call(refreshContent);
    yield put(showSnackbar({ message: "File Type Updated" }));
  } catch (error) {
    yield all([
      put(
        registerError([
          {
            system: error,
            user: "An error occurred updating file type"
          }
        ])
      )
    ]);
  } finally {
    yield put(actions.setShowEditTypeModal(false));
  }
};

const showEditDocumentPage = function*({ payload: contentId }) {
  const { eventDetails } = yield call(getParams);

  const returnTo = window.location.pathname;

  yield put(
    push(
      `/event-light/${eventDetails.id}/files/${contentId}/edit?returnTo=${returnTo}`
    )
  );
};

const watchInit = function*() {
  yield takeEvery(actions.init.type, init);
};

const watchRefreshContent = function*() {
  yield takeEvery(actions.refreshContent.type, refreshContent);
};

const watchSendComment = function*() {
  yield takeEvery(actions.sendComment.type, sendComment);
};

const watchDownloadFile = function*() {
  yield takeEvery(actions.downloadFile.type, downloadFile);
};

const watchDeleteFile = function*() {
  yield takeEvery(actions.deleteFile.type, deleteFile);
};

const watchDeleteAllVersions = function*() {
  yield takeEvery(actions.deleteAllVersions.type, deleteAllVersions);
};

const watchSaveUpdatedType = function*() {
  yield takeEvery(actions.saveUpdatedType.type, saveUpdatedType);
};

const watchUpdateFields = function*() {
  for (;;) {
    const {
      meta: { instanceId }
    } = yield take(FormActions.setFieldValue.type);
    if (instanceId === EDIT_TYPE_FORM_ID) {
      const settings = yield select(getters.settings);
      let shouldDisableButton = false;
      if (!settings?.bizzabo_integration_enabled) {
        const fields = yield select(FormGetters.fields, {
          instanceId: EDIT_TYPE_FORM_ID
        });
        shouldDisableButton = Object.values(EDIT_FILE_TYPE_FIELD_ID).reduce(
          (acc, field) => {
            if (!fields[field] || !fields[field].value.length) {
              return true;
            }
            return acc;
          },
          false
        );
      }
      yield put(actions.setIsSaveBtnDisabled(shouldDisableButton));
    }
  }
};

const watchShowEditTypeModal = function*() {
  yield takeEvery(actions.setShowEditTypeModal.type, checkEditTypeModalOpening);
};

const watchShowEditDocumentPage = function*() {
  yield takeEvery(actions.showEditDocumentPage.type, showEditDocumentPage);
};

const rootSaga = function*() {
  yield all([
    fork(fileUploadSagas),
    fork(watchInit),
    fork(watchRefreshContent),
    fork(watchSendComment),
    fork(watchDownloadFile),
    fork(watchDeleteFile),
    fork(watchDeleteAllVersions),
    fork(watchSaveUpdatedType),
    fork(watchShowEditTypeModal),
    fork(watchUpdateFields),
    fork(watchShowEditDocumentPage)
  ]);
};

export default rootSaga;
